/**
 * @fileoverview Pre-Appointment pipeline kanban board (MOR-882)
 *
 * dnd-kit board over pipeline jobs. Columns come from the active group-by
 * dimension: pipeline stage (drag persists the stage + manual board rank),
 * referrer, or appointment type (display-only columns — a job's referrer /
 * type is edited in the side panel, not by dragging). Card drops keep an
 * optimistic placement until Convex refreshes, so the card does not snap
 * back to the source column. Mirrors a slimmed-down
 * `src/calendar/components/board/task-board.tsx`.
 */

import { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { Handshake, ShieldAlert } from "lucide-react";

import type { Id } from "@cvx/_generated/dataModel";
import type { PreAppointmentViewGroupBy } from "@cvx/preAppointment/preAppointmentViews/schema";

import {
  APPOINTMENT_TYPE_LABELS,
  NO_REFERRER_KEY,
  NO_TYPE_KEY,
  UNSTAGED_KEY,
  type PipelineJob,
  type PipelineLabel,
  type PipelineStatus,
} from "./constants";
import {
  applyPendingPlacement,
  serverCaughtUpToPlacement,
  type PipelinePendingPlacement,
} from "./pipeline-board-placement";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { CustomFieldBadge } from "./custom-field-badge";
import { type CustomFieldDef, customFieldTotals } from "./custom-fields";
import { cn } from "@/utils/misc";

export interface PipelineBoardMoveArgs {
  jobId: Id<"jobs">;
  /** Target stage (only for `groupBy === "status"` drags). */
  statusId: Id<"preAppointmentStatuses"> | null;
  beforeJobId?: Id<"jobs">;
  afterJobId?: Id<"jobs">;
}

interface Column {
  key: string;
  title: string;
  color?: BadgeColor;
  jobs: PipelineJob[];
}

/** Sort cards within a column: manual board rank first, then newest. */
function compareCards(a: PipelineJob, b: PipelineJob): number {
  if (a.preAppointmentBoardRank && b.preAppointmentBoardRank) {
    return a.preAppointmentBoardRank < b.preAppointmentBoardRank ? -1 : 1;
  }
  if (a.preAppointmentBoardRank) return -1;
  if (b.preAppointmentBoardRank) return 1;
  return b._creationTime - a._creationTime;
}

interface BoardDrop {
  columnKey: string;
  insertIndex: number;
  beforeJobId?: Id<"jobs">;
  afterJobId?: Id<"jobs">;
}

/** Target column and neighbours for a pipeline card drop. */
function resolveBoardDrop(
  columns: ReadonlyArray<Column>,
  jobId: string,
  overId: string,
): BoardDrop | null {
  if (overId.startsWith("column:")) {
    const columnKey = overId.slice("column:".length);
    const col = columns.find((c) => c.key === columnKey);
    if (!col) return null;
    const siblings = col.jobs.filter((j) => String(j._id) !== jobId);
    const last = siblings.at(-1);
    return {
      columnKey,
      insertIndex: siblings.length,
      beforeJobId: last?._id,
    };
  }

  if (overId === jobId) return null;
  const overCol = columns.find((c) =>
    c.jobs.some((j) => String(j._id) === overId),
  );
  if (!overCol) return null;
  const siblings = overCol.jobs.filter((j) => String(j._id) !== jobId);
  const idx = siblings.findIndex((j) => String(j._id) === overId);
  if (idx < 0) return null;
  const overJob = siblings[idx];
  return {
    columnKey: overCol.key,
    insertIndex: idx,
    afterJobId: overJob?._id,
    beforeJobId: idx > 0 ? siblings[idx - 1]?._id : undefined,
  };
}

export function PipelineBoard({
  jobs,
  statuses,
  labels,
  customFields,
  groupBy,
  onCardClick,
  onMove,
  onOpenReferrer,
}: {
  jobs: PipelineJob[];
  statuses: PipelineStatus[];
  labels: PipelineLabel[];
  customFields: CustomFieldDef[];
  groupBy: PreAppointmentViewGroupBy;
  onCardClick: (job: PipelineJob) => void;
  onMove: (args: PipelineBoardMoveArgs) => Promise<void>;
  /** Open the referrer's firm-contact drawer (mail's contact-panel pattern). */
  onOpenReferrer: (firmContactId: Id<"firmContacts">) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  // Keep the card in the drop column until Convex's list query observes
  // the mutation — otherwise dnd-kit's overlay flies home and the card
  // flickers in the source stage.
  const [pendingMove, setPendingMove] =
    useState<PipelinePendingPlacement | null>(null);

  const labelById = useMemo(
    () => new Map(labels.map((l) => [String(l._id), l])),
    [labels],
  );

  const columns = useMemo<Column[]>(() => {
    const buckets = new Map<string, PipelineJob[]>();
    const push = (key: string, job: PipelineJob) => {
      const list = buckets.get(key) ?? [];
      list.push(job);
      buckets.set(key, list);
    };

    if (groupBy === "referrer") {
      for (const job of jobs) {
        push(job.referrer ? String(job.referrer._id) : NO_REFERRER_KEY, job);
      }
      const referrerCols: Column[] = [];
      for (const [key, colJobs] of buckets) {
        if (key === NO_REFERRER_KEY) continue;
        referrerCols.push({
          key,
          title: colJobs[0]?.referrer?.displayName ?? "Referrer",
          jobs: colJobs.sort(compareCards),
        });
      }
      referrerCols.sort((a, b) => a.title.localeCompare(b.title));
      return [
        ...referrerCols,
        {
          key: NO_REFERRER_KEY,
          title: "No referrer",
          jobs: (buckets.get(NO_REFERRER_KEY) ?? []).sort(compareCards),
        },
      ];
    }

    if (groupBy === "appointmentType") {
      for (const job of jobs) {
        push(job.appointmentType ?? NO_TYPE_KEY, job);
      }
      const typeCols: Column[] = [...buckets.entries()]
        .filter(([key]) => key !== NO_TYPE_KEY)
        .map(([key, colJobs]) => ({
          key,
          title:
            APPOINTMENT_TYPE_LABELS[
              key as keyof typeof APPOINTMENT_TYPE_LABELS
            ] ?? key,
          jobs: colJobs.sort(compareCards),
        }))
        .sort((a, b) => a.title.localeCompare(b.title));
      return [
        ...typeCols,
        {
          key: NO_TYPE_KEY,
          title: "No type yet",
          jobs: (buckets.get(NO_TYPE_KEY) ?? []).sort(compareCards),
        },
      ];
    }

    // Default: one column per pipeline stage, in stage order, plus a
    // trailing "Unstaged" column for jobs not yet placed.
    for (const job of jobs) {
      push(
        job.preAppointmentStatusId
          ? String(job.preAppointmentStatusId)
          : UNSTAGED_KEY,
        job,
      );
    }
    const stageCols: Column[] = statuses.map((s) => ({
      key: String(s._id),
      title: s.name,
      color: s.color as BadgeColor,
      jobs: (buckets.get(String(s._id)) ?? []).sort(compareCards),
    }));
    const unstaged = (buckets.get(UNSTAGED_KEY) ?? []).sort(compareCards);
    return unstaged.length > 0
      ? [{ key: UNSTAGED_KEY, title: "Unstaged", jobs: unstaged }, ...stageCols]
      : stageCols;
  }, [jobs, statuses, groupBy]);

  const displayColumns = useMemo(
    () => applyPendingPlacement(columns, pendingMove),
    [columns, pendingMove],
  );

  useEffect(() => {
    if (!pendingMove) return;
    if (serverCaughtUpToPlacement(jobs, pendingMove)) {
      setPendingMove(null);
    }
  }, [jobs, pendingMove]);

  const jobById = useMemo(
    () => new Map(jobs.map((j) => [String(j._id), j])),
    [jobs],
  );
  const activeJob = activeId ? jobById.get(activeId) : undefined;
  const dragEnabled = groupBy === "status" || groupBy === "none";

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || !dragEnabled) return;

    const jobId = String(active.id);
    const overId = String(over.id);
    const dragged = jobById.get(jobId);
    if (!dragged) return;

    const drop = resolveBoardDrop(displayColumns, jobId, overId);
    if (!drop) return;

    const sourceCol = displayColumns.find((c) =>
      c.jobs.some((j) => String(j._id) === jobId),
    );
    const sourceIndex =
      sourceCol?.jobs.findIndex((j) => String(j._id) === jobId) ?? -1;
    if (sourceCol?.key === drop.columnKey && sourceIndex === drop.insertIndex) {
      return;
    }

    setPendingMove({
      jobId,
      columnKey: drop.columnKey,
      insertIndex: drop.insertIndex,
      prevStatusId: dragged.preAppointmentStatusId
        ? String(dragged.preAppointmentStatusId)
        : null,
      prevRank: dragged.preAppointmentBoardRank ?? null,
    });

    const droppedStatus = statuses.find(
      (s) => String(s._id) === drop.columnKey,
    );
    void onMove({
      jobId: dragged._id,
      statusId: groupBy === "status" ? (droppedStatus?._id ?? null) : null,
      beforeJobId: drop.beforeJobId,
      afterJobId: drop.afterJobId,
    }).catch(() => {
      setPendingMove(null);
    });
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={handleDragEnd}
    >
      {/*
        Native scroll container (mirrors the calendar task board) rather than
        a Radix ScrollArea: Radix hides the viewport's overflow-x unless a
        horizontal scrollbar is mounted, which clipped every column past the
        viewport edge. `w-max` sizes the row to its columns so it can scroll.
      */}
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex w-max items-start gap-4 pb-4">
          {displayColumns.map((col) => (
            <BoardColumn
              key={col.key}
              column={col}
              labelById={labelById}
              customFields={customFields}
              dragEnabled={dragEnabled}
              onCardClick={onCardClick}
              onOpenReferrer={onOpenReferrer}
            />
          ))}
        </div>
      </div>
      {/*
        No drop animation: the overlay would fly back to the source column
        (still showing the pre-mutation query) and the card would flicker.
        Optimistic placement already puts the card in the destination.
      */}
      <DragOverlay dropAnimation={null}>
        {activeJob ? (
          <PipelineCard
            job={activeJob}
            labelById={labelById}
            customFields={customFields}
            dragging
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function BoardColumn({
  column,
  labelById,
  customFields,
  dragEnabled,
  onCardClick,
  onOpenReferrer,
}: {
  column: Column;
  labelById: Map<string, PipelineLabel>;
  customFields: CustomFieldDef[];
  dragEnabled: boolean;
  onCardClick: (job: PipelineJob) => void;
  onOpenReferrer: (firmContactId: Id<"firmContacts">) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `column:${column.key}` });
  // What this stage is worth, for the fields that can be summed (MOR-948).
  const totals = customFieldTotals(customFields, column.jobs);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex w-72 shrink-0 flex-col rounded-lg border bg-background/60 p-2",
        isOver && dragEnabled && "ring-2 ring-primary/40",
      )}
    >
      <div className="flex items-center gap-2 px-1.5 pb-2 pt-1">
        {column.color ? (
          <Badge variant="soft" color={column.color} size="sm">
            {column.title}
          </Badge>
        ) : (
          <span className="text-sm font-medium">{column.title}</span>
        )}
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">
          {column.jobs.length}
        </span>
      </div>
      {totals.length > 0 && (
        <div className="flex flex-wrap gap-1 px-1.5 pb-2">
          {totals.map(({ field, total }) => (
            <CustomFieldBadge
              key={field._id}
              field={field}
              value={String(total)}
              showName
            />
          ))}
        </div>
      )}
      <div className="flex min-h-16 flex-col gap-2">
        {column.jobs.map((job) => (
          <DraggableCard
            key={String(job._id)}
            job={job}
            labelById={labelById}
            customFields={customFields}
            dragEnabled={dragEnabled}
            onClick={() => onCardClick(job)}
            onOpenReferrer={onOpenReferrer}
          />
        ))}
        {column.jobs.length === 0 && (
          <div className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">
            No jobs
          </div>
        )}
      </div>
    </div>
  );
}

function DraggableCard({
  job,
  labelById,
  customFields,
  dragEnabled,
  onClick,
  onOpenReferrer,
}: {
  job: PipelineJob;
  labelById: Map<string, PipelineLabel>;
  customFields: CustomFieldDef[];
  dragEnabled: boolean;
  onClick: () => void;
  onOpenReferrer: (firmContactId: Id<"firmContacts">) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: String(job._id),
    disabled: !dragEnabled,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(isDragging && "invisible")}
      onClick={onClick}
    >
      <PipelineCard
        job={job}
        labelById={labelById}
        customFields={customFields}
        onOpenReferrer={onOpenReferrer}
      />
    </div>
  );
}

function PipelineCard({
  job,
  labelById,
  customFields,
  dragging,
  onOpenReferrer,
}: {
  job: PipelineJob;
  labelById: Map<string, PipelineLabel>;
  customFields: CustomFieldDef[];
  dragging?: boolean;
  onOpenReferrer?: (firmContactId: Id<"firmContacts">) => void;
}) {
  const jobLabels = job.preAppointmentLabelIds
    .map((id) => labelById.get(String(id)))
    .filter((l): l is PipelineLabel => Boolean(l));

  // Custom fields in the firm's configured order, skipping any this job
  // hasn't been given a value for (MOR-948).
  const fieldValues = customFields
    .map((field) => ({
      field,
      value:
        job.preAppointmentCustomFields[
          field._id as keyof typeof job.preAppointmentCustomFields
        ],
    }))
    .filter((entry): entry is { field: CustomFieldDef; value: string } =>
      Boolean(entry.value),
    );

  return (
    <div
      className={cn(
        "cursor-pointer rounded-md border bg-card p-3 shadow-sm transition-shadow hover:shadow",
        dragging && "shadow-lg",
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{job.entityName}</p>
          {job.jobName && (
            <p className="truncate text-xs text-muted-foreground">
              {job.jobName}
            </p>
          )}
        </div>
        {job.openConflictCheckCount > 0 && (
          <span
            className="flex items-center gap-0.5 text-amber-600"
            title={`${job.openConflictCheckCount} open conflict check${job.openConflictCheckCount === 1 ? "" : "s"}`}
          >
            <ShieldAlert className="size-3.5" />
          </span>
        )}
      </div>
      {fieldValues.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {fieldValues.map(({ field, value }) => (
            <CustomFieldBadge key={field._id} field={field} value={value} />
          ))}
        </div>
      )}
      {(job.appointmentType || jobLabels.length > 0) && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {job.appointmentType && (
            <Badge variant="outline" size="sm">
              {APPOINTMENT_TYPE_LABELS[job.appointmentType]}
            </Badge>
          )}
          {jobLabels.map((label) => (
            <Badge
              key={String(label._id)}
              variant="soft"
              size="sm"
              color={label.color as BadgeColor}
            >
              {label.name}
            </Badge>
          ))}
        </div>
      )}
      {job.referrer && (
        <button
          type="button"
          className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            if (job.referrer) onOpenReferrer?.(job.referrer._id);
          }}
        >
          <Handshake className="size-3" />
          <span className="truncate">
            {job.referrer.displayName}
            {job.referrer.parentDisplayName
              ? ` (${job.referrer.parentDisplayName})`
              : ""}
          </span>
        </button>
      )}
    </div>
  );
}
